import { ref } from 'vue'
import type {
  Dialogs,
  DialogOptions,
  DialogEvents,
  DialogRegistryOptions,
  DialogRuntimeOptions,
} from './types'
import { useDevice } from '@/shared/composables/useDevice'
import { resolveDialogOptions } from './registry'

export interface ActiveDialog
  extends DialogOptions,
    DialogRegistryOptions,
    DialogRuntimeOptions {
  id: number
  visible: boolean
}

let nextId = 0

const dialogs = ref<ActiveDialog[]>([])
const isOpen = ref(false)

const isMobileBottomSheet = (d?: ActiveDialog) => {
  const { isMobile } = useDevice()
  return isMobile.value && d?.presentation !== 'modal'
}

export function useDialog() {
  const open = async (name: Dialogs, opts?: Omit<DialogOptions, 'name'>) => {
    const registryOptions = resolveDialogOptions(name)
    const dialog: ActiveDialog = {
      name,
      props: opts?.props,
      stack: opts?.stack,
      events: opts?.events,
      presentation: registryOptions.presentation ?? 'adaptive',
      modal: registryOptions.modal,
      slideup: { height: 'content', ...registryOptions.slideup },
      id: nextId++,
      visible: true,
    }

    if (opts?.stack) {
      dialogs.value.push(dialog)
    } else if (isOpen.value && dialogs.value.length) {
      const current = dialogs.value.at(-1)
      if (isMobileBottomSheet(current)) {
        // Current is a bottom sheet: slide it out first, then show new one
        current!.visible = false
        await new Promise((r) => setTimeout(r, 400))
      }
      // Check state hasn't been cleared by closeAll during the wait
      if (dialogs.value.length) {
        dialogs.value.at(-1)?.events?.close?.()
      }
      dialogs.value = [dialog]
    } else {
      dialogs.value = [dialog]
    }
    isOpen.value = true
  }

  const close = async (data?: unknown) => {
    const top = dialogs.value.at(-1)
    if (!top) return

    top.events?.close?.(data)

    if (isMobileBottomSheet(top) && dialogs.value.length > 1) {
      // Stacked mobile bottom sheet: animate horizontal slide back, then remove
      top.visible = false
      await new Promise((r) => setTimeout(r, 300))
    }

    // Check state hasn't been cleared during the wait
    if (!dialogs.value.length) return

    dialogs.value.pop()

    if (!dialogs.value.length) {
      // Clear dialogs first (triggers leave transition), then hide after animation
      await new Promise((r) => setTimeout(r, 400))
      isOpen.value = false
    }
  }

  const closeAll = async () => {
    // Clear dialogs (triggers leave transitions), then hide after animation
    dialogs.value = []
    await new Promise((r) => setTimeout(r, 400))
    isOpen.value = false
  }

  const triggerEvent = (event: keyof DialogEvents, data?: unknown) => {
    const fn = dialogs.value.at(-1)?.events?.[event]
    fn?.(data)
    return !!fn
  }

  const updateOptions = (options: DialogRuntimeOptions, id?: number) => {
    const dialog =
      id !== undefined
        ? dialogs.value.find((dialog) => dialog.id === id)
        : dialogs.value.at(-1)
    if (!dialog) return

    Object.assign(dialog, options)
  }

  return { dialogs, isOpen, open, close, closeAll, triggerEvent, updateOptions }
}
